import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import Login from "../view/user/login/Login";
// import DemoAudioAnalytics from "../view/user/audioAnalytics";
import Layout from "../layout";
import { ProtectedRoute, RoleBasedRedirect } from "../routes/ProtectedRoute";
import NotFound from "../../src/components/PageNotFound"
import { Dashboard } from "@mui/icons-material";

// Route pages are code-split: each page (and the heavy chart/grid/export libraries only it uses)
// is downloaded when first visited instead of in one up-front bundle. The <Suspense> boundaries
// live in App.js (top-level routes) and layout/index.js (pages inside the app shell).
const Register = lazy(() => import("../view/demo/Register/Register"));
const Account = lazy(() => import("../view/user/account"));
const Audio = lazy(() => import("../view/user/audio"));
const AudioTable = lazy(() => import("../view/user/audio/AudioTable"));
const Setting = lazy(() => import("../view/user/setting"));
const DashBoard = lazy(() => import("../view/user/dashboard"));
const FileUpload = lazy(() => import("../view/demo/audio/FileUpload"));
const SingleAudioDetails = lazy(() => import("../view/demo/audio/SingleAudioDetails"));
const TranscriptDetails = lazy(() => import("../view/demo/audio/TranscriptDetails"));
const SuperAdminDashboard = lazy(() => import("../view/superAdmin/dashboard"));
const SuperAdminUserManagement = lazy(() => import("../view/superAdmin/userManagement"));
const SuperAdminProcessManagement = lazy(() => import("../view/superAdmin/processManagement"));
const SuperAdminClientManagement = lazy(() => import("../view/superAdmin/clientManagement"));
const RolesPermissions = lazy(() => import("../view/rolesPermissions"));
const SuperAdminCronManagement = lazy(() => import("../view/superAdmin/cronManagement"));
const SuperAdminKeywordAlerts = lazy(() => import("../view/superAdmin/keywordAlerts"));
const SuperAdminAgentCallEmails = lazy(() => import("../view/superAdmin/agentCallEmails"));
const SuperAdminSttCredentialManagement = lazy(() => import("../view/superAdmin/sttCredentialManagement"));
const SuperAdminLlmCredentialManagement = lazy(() => import("../view/superAdmin/llmCredentialManagement"));
const SuperAdminOpenRouterQa = lazy(() => import("../view/superAdmin/openRouterQa"));
const SuperAdminOpenRouterQaDetail = lazy(() => import("../view/superAdmin/openRouterQa/OpenRouterQaDetail"));
const SuperAdminAiEngineModeManagement = lazy(() => import("../view/superAdmin/aiEngineModeManagement"));
const ArrangeQaParameterLegacy = lazy(() => import("../view/superAdmin/arrangeQaParameter"));
const QaParameterJsonLegacy = lazy(() => import("../view/superAdmin/qaParameterJson"));
const ArrangeQaParameter = lazy(() => import("../view/superAdmin/qaParameterTaxonomy"));
const QaParameterJson = lazy(() => import("../view/superAdmin/qaParameterTaxonomyJson"));
const CompanyAccess = lazy(() => import("../view/superAdmin/companyAccess"));
const Billing = lazy(() => import("../view/superAdmin/billingAnalytics"));
const AudioIntelligence = lazy(() => import("../view/user/audioIntelligence"));
const AgentCreateForm = lazy(() => import("../view/superAdmin/agentCreate/AgentCreateForm"));
const SuperAdminUpdateTracker = lazy(() => import("../view/superAdmin/updateTracker"));
const AgentDashBoard = lazy(() => import("../view/agent/dashboard"));
const MyKpis = lazy(() => import("../view/agent/kpis"));
const MyCalls = lazy(() => import("../view/agent/myCalls"));
const DailyTask = lazy(() => import("../view/agent/dailyTask"));
const LeaderBoardHome = lazy(() => import("../view/agent/leaderboard"));
const Subscription = lazy(() => import("../view/Subscription"));
const LeaderBoard = lazy(() => import("../view/user/LeaderBoard"));
const Settings = lazy(() => import("../view/settings"));
const Billings = lazy(() => import("../view/user/Billing"));
const AgentProfile = lazy(() => import("../view/user/agentProfile"));
const UserManagement = lazy(() => import("../../src/view/user/userManagement/index"));
const AgentAssignment = lazy(() => import("../view/user/agentAssignment"));
const AgentList = lazy(() => import("../../src/view/user/AgentList"));
const Dashboard2 = lazy(() => import("../view/user/dashboard2"));
const DashboardNew = lazy(() => import("../view/user/dashboardNew"));
const Training = lazy(() => import("../view/user/training"));
const AccountManagement = lazy(() => import("../view/user/AccountManagement"));
const ChatbotUI = lazy(() => import("../view/agent/chatbot"));
const Invoices = lazy(() => import("../view/superAdmin/billingAnalytics/invoice"));
const QAParametersModal = lazy(() => import("../components/commonQA"));
const EntityConfigModal = lazy(() => import("../components/commonEntities"));
const RediarizationPrompt = lazy(() => import("../components/commonRediarization"));
const OverViewPrompt = lazy(() => import("../components/commonOverviewPrompt"));
const CustomerInsightPrompt = lazy(() => import("../components/commonCustomerInsightPrompt"));
const AgantInsightPrompt = lazy(() => import("../components/commonAgentInsightPrompt"));
const ConversionInsightPrompt = lazy(() => import("../components/commonConversion"));
const MPokketDashboard = lazy(() => import("../view/user/mPokketDashboard"));
const QaEditPage = lazy(() => import("../view/user/qaEdit"));
const VoiceCapture = lazy(() => import("../view/user/voice-capture/VoiceCapture"));
const PipelineStatus = lazy(() => import("../view/pipelineStatus"));
const ManualAudit = lazy(() => import("../view/user/manualAudit"));
const ManualAuditNew = lazy(() => import("../view/user/manualAuditNew"));
const ManualAuditFieldManagement = lazy(() => import("../view/user/manualAuditFields"));
const ManualAuditOptionManagement = lazy(() => import("../view/user/manualAuditOptions"));
const ManualDashboard = lazy(() => import("../view/user/manualDashboard"));
const CuratorCallAnalysisImport = lazy(() => import("../view/user/curatorCallAnalysisImport"));
const KnowledgePortal = lazy(() => import("../view/user/knowledgePortal"));
const SensitiveKeywordAlerts = lazy(() => import("../view/user/sensitiveKeywordAlerts"));
const FeedbackAcceptance = lazy(() => import("../view/user/feedbackAcceptance"));
const GoodCallLibrary = lazy(() => import("../view/user/goodCallLibrary"));
const ActivityLogs = lazy(() => import("../view/activityLogs"));


// Every non-Agent/Curator/SuperAdmin role gets the same manager-panel access today —
// there's no fine-grained permission system, so all org-tier roles share this list.
const MANAGER_PANEL_ROLES = [
  "Manager",
  "Admin",
  "Curator",
  "TeamLeader",
  "QualityAnalyst",
  "Trainer",
  "AssistantManagerTraining",
  "ManagerTraining",
  "SeniorManagerTraining",
  "AssistantManagerQuality",
  "ManagerQuality",
  "SeniorManagerQuality",
  "AssistantManagerOps",
  "ManagerOps",
  "SeniorManagerOps",
  "AssistantVicePresident",
  "SeniorVicePresident",
  "AssistantManager",
  "SeniorAssistantManager",
  "SeniorTrainer",
  "SeniorManager",
];

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        index: true,
        element: <RoleBasedRedirect />,
      },
      {
        path: "/user/dashboard-new",
        element: (
          <ProtectedRoute element={<Dashboard2 />} requiredPage="dashboard" />
        ),
      },
      {
        path: "/user/dashboard",
        element: (
          <ProtectedRoute element={<DashboardNew />} requiredPage="dashboard" />
        ),
      },
      // {
      //   path: "/user/mpokket-dashboard",
      //   element: (
      //     <ProtectedRoute element={<MPokketDashboard />} allowedRoles={MANAGER_PANEL_ROLES} />
      //   ),
      // },

      {
        path: "/user/dashboard2",
        element: (
          <ProtectedRoute element={<Dashboard />} requiredPage="dashboard" />
        ),
      },
      {
        path: "/user/audio",
        element: (
          <ProtectedRoute element={<Audio />} requiredPage="interactions" />
        ),
      },
      {
        path: "/user/account",
        element: (
          <ProtectedRoute element={<Account />} allowedRoles={MANAGER_PANEL_ROLES} />
        ),
      },
      {
        path: "/user/setting",
        element: (
          <ProtectedRoute element={<Setting />} requiredPage="myProfile" />
        ),
      },
      {
        path: "/user/leaderboard",
        element: (
          <ProtectedRoute
            element={<LeaderBoard />}
            requiredPage="leaderboard"
          />
        ),
      },
      {
        path: "/user/audio-details",
        element: (
          <ProtectedRoute element={<AudioTable />} requiredPage="interactions" />
        ),
      },
      {
        path: "/user/manual-audit",
        element: (
          <ProtectedRoute element={<ManualAudit />} requiredPage="manualAudit" />
        ),
      },
      {
        path: "/user/manual-audit-new",
        element: (
          <ProtectedRoute element={<ManualAuditNew />} requiredPage="manualAudit" />
        ),
      },
      {
        // Manual Dashboard (AOA variance), gated by the 'manualAuditDashboard' matrix key. SuperAdmin
        // fast-path mirrors the backend's requirePagePermissionUnlessSuperAdmin.
        path: "/user/manual-dashboard",
        element: (
          <ProtectedRoute
            element={<ManualDashboard />}
            requiredPage="manualAuditDashboard"
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        // Gated by the 'manualAuditFields' Roles & Permissions matrix key — an Admin grants
        // per-role access from the Roles & Permissions screen. allowedRoles={["SuperAdmin"]}
        // is an OR fast-path (see ProtectedRoute) so SuperAdmin always gets in regardless of
        // the matrix, same as the backend's requirePagePermissionUnlessSuperAdmin.
        path: "/user/manual-audit-fields",
        element: (
          <ProtectedRoute
            element={<ManualAuditFieldManagement />}
            requiredPage="manualAuditFields"
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        // Manual Audit Options panel (centers, buckets + LOB, dispositions, VOC, … stored in MongoDB) —
        // gated by the 'manualAuditOptions' permission key (on for Admin by default), SuperAdmin always.
        path: "/user/manual-audit-options",
        element: (
          <ProtectedRoute
            element={<ManualAuditOptionManagement />}
            requiredPage="manualAuditOptions"
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        // Read-only Collection Knowledge Bank, gated by the 'knowledgePortal' matrix key (backfilled
        // true for every existing role). SuperAdmin fast-path mirrors the backend's
        // requirePagePermissionUnlessSuperAdmin.
        path: "/user/knowledge-portal",
        element: (
          <ProtectedRoute
            element={<KnowledgePortal />}
            requiredPage="knowledgePortal"
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        // Calls a sensitive-keyword email went out for, gated by the 'sensitiveKeywordAlerts'
        // matrix key. SuperAdmin fast-path mirrors the backend's requirePagePermissionUnlessSuperAdmin.
        path: "/user/sensitive-keyword-alerts",
        element: (
          <ProtectedRoute
            element={<SensitiveKeywordAlerts />}
            requiredPage="sensitiveKeywordAlerts"
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        // Agents' accept / rebuttal responses to the feedback emails, gated by the 'feedbackAcceptance'
        // matrix key. SuperAdmin fast-path mirrors the backend's requirePagePermissionUnlessSuperAdmin.
        path: "/user/feedback-acceptance",
        element: (
          <ProtectedRoute
            element={<FeedbackAcceptance />}
            requiredPage="feedbackAcceptance"
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        // Best-practice calls to listen to, gated by the 'goodCallLibrary' matrix key (on for every
        // manager-panel role and Agent); 'goodCallLibraryManage' adds the curating controls inside the
        // page. SuperAdmin fast-path mirrors the backend's requirePagePermissionUnlessSuperAdmin.
        path: "/user/good-call-library",
        element: (
          <ProtectedRoute
            element={<GoodCallLibrary />}
            requiredPage="goodCallLibrary"
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        // Hardcoded to Curator only (bypasses the permission matrix, same pattern as
        // /user/manual-audit-fields) — mirrors the backend's requireCuratorRole gate on
        // POST /curator/call-analysis/import-excel, which deliberately has no Roles &
        // Permissions matrix entry either.
        path: "/user/curator/call-analysis-import",
        element: (
          <ProtectedRoute element={<CuratorCallAnalysisImport />} allowedRoles={["Curator"]} />
        ),
      },
      {
        path: "/user/qa-edit/:id",
        element: (
          <ProtectedRoute
            element={<QaEditPage />}
            requiredPage="interactions"
          />
        ),
      },
      {
        // Shared with the Agent's own view — allowedRoles={["Agent"]} lets an Agent
        // through without needing the "interactions" page permission, which they don't have.
        path: "/user/audio-analytics/:id",
        element: (
          <ProtectedRoute
            element={<AudioIntelligence />}
            allowedRoles={["Agent"]}
            requiredPage="interactions"
          />
        ),
      },
      {
        path: "/user/audio-intelligence",
        element: (
          <ProtectedRoute
            element={<AudioIntelligence />}
            requiredPage="interactions"
          />
        ),
      },
      {
        path: "/user/agent-profile/:id",
        element: (
          <ProtectedRoute element={<AgentProfile />} requiredPage="agentProfile" />
        ),
      },
      {
        path: "/user/settings",
        element: (
          <ProtectedRoute element={<Settings />} allowedRoles={MANAGER_PANEL_ROLES} />
        ),
      },
      // {
      //   path: "/user/billing",
      //   element: (
      //     <ProtectedRoute element={<Billings />} allowedRoles={MANAGER_PANEL_ROLES} />
      //   ),
      // },
      {
        path: "/user/user-management",
        element: (
          <ProtectedRoute
            element={<UserManagement />}
            requiredPage="userManagement"
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/user/agent-assignment",
        element: (
          <ProtectedRoute
            element={<AgentAssignment />}
            requiredPage="agentAssignment"
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        // Hardcoded to Admin only (bypasses the permission matrix) — mirrors the backend's
        // requirePermissionEditorRole, so an Admin can never lock themselves out of this page.
        path: "/user/roles-permissions",
        element: (
          <ProtectedRoute element={<RolesPermissions />} allowedRoles={["Admin"]} />
        ),
      },
      // {
      //   path: "/user/agent/list",
      //   element: (
      //     <ProtectedRoute element={<AgentList />} allowedRoles={["Manager", "Admin",]} />
      //   ),
      // },

      {
        path: "/user/training",
        element: (
          <ProtectedRoute element={<Training />} requiredPage="training" />
        ),
      },
      {
        path: "/user/account/manage",
        element: (
          <ProtectedRoute element={<AccountManagement />} requiredPage="accountSettings" />
        ),
      },
      //// demo user
      // {
      //   path: "/demo/audio-analytics/:id",
      //   element: (
      //     <ProtectedRoute
      //       element={<DemoAudioAnalytics />}
      //       allowedRoles={["Guest"]}
      //     />
      //   ),
      // },
      {
        path: "/demo/audio/upload-file",
        element: (
          <ProtectedRoute element={<FileUpload />} allowedRoles={["Guest"]} />
        ),
      },
      {
        path: "/demo/audio/file-details",
        element: (
          <ProtectedRoute
            element={<SingleAudioDetails />}
            allowedRoles={["Guest"]}
          />
        ),
      },
      {
        path: "/demo/audio/transcript/:id",
        element: (
          <ProtectedRoute
            element={<TranscriptDetails />}
            allowedRoles={["Guest"]}
          />
        ),
      },
      /// super-admin routes
      {
        path: "/super-admin/dashboard",
        element: (
          <ProtectedRoute
            element={<SuperAdminDashboard />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/process-management",
        element: (
          <ProtectedRoute
            element={<SuperAdminProcessManagement />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/client-management",
        element: (
          <ProtectedRoute
            element={<SuperAdminClientManagement />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/roles-permissions",
        element: (
          <ProtectedRoute
            element={<RolesPermissions />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/arrange-qa-parameter-legacy",
        element: (
          <ProtectedRoute
            element={<ArrangeQaParameterLegacy />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/qa-parameter-json-legacy",
        element: (
          <ProtectedRoute
            element={<QaParameterJsonLegacy />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/arrange-qa-parameter",
        element: (
          <ProtectedRoute
            element={<ArrangeQaParameter />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/qa-parameter-json",
        element: (
          <ProtectedRoute
            element={<QaParameterJson />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/agent-create",
        element: (
          <ProtectedRoute element={<AgentCreateForm />} allowedRoles={["SuperAdmin"]} />
        ),
      },
      {
        path: "/super-admin/qa-parameter-prompt/:companyId/:processType",
        element: (
          <ProtectedRoute
            element={<QAParametersModal />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "super-admin/entities-prompt/:companyId/:processType",
        element: (
          <ProtectedRoute
            element={<EntityConfigModal />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "super-admin/rediarization-prompt/:companyId/:processType",
        element: (
          <ProtectedRoute
            element={<RediarizationPrompt />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "super-admin/overview-prompt/:companyId/:processType",
        element: (
          <ProtectedRoute
            element={<OverViewPrompt />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "super-admin/customer-insight-prompt/:companyId/:processType",
        element: (
          <ProtectedRoute
            element={<CustomerInsightPrompt />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "super-admin/agent-insight-prompt/:companyId/:processType",
        element: (
          <ProtectedRoute
            element={<AgantInsightPrompt />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "super-admin/conversion-insight-prompt/:companyId/:processType",
        element: (
          <ProtectedRoute
            element={<ConversionInsightPrompt />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/user-management",
        element: (
          <ProtectedRoute
            element={<SuperAdminUserManagement />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/company-access",
        element: (
          <ProtectedRoute
            element={<CompanyAccess />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/billing",
        element: (
          <ProtectedRoute element={<Billing />} allowedRoles={["SuperAdmin"]} />
        ),
      },
      {
        path: "/super-admin/cron-management",
        element: (
          <ProtectedRoute
            element={<SuperAdminCronManagement />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/keyword-alerts",
        element: (
          <ProtectedRoute
            element={<SuperAdminKeywordAlerts />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/agent-call-emails",
        element: (
          <ProtectedRoute
            element={<SuperAdminAgentCallEmails />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/stt-credential-management",
        element: (
          <ProtectedRoute
            element={<SuperAdminSttCredentialManagement />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/llm-credential-management",
        element: (
          <ProtectedRoute
            element={<SuperAdminLlmCredentialManagement />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        path: "/super-admin/openrouter-qa",
        element: (
          <ProtectedRoute
            element={<SuperAdminOpenRouterQa />}
            allowedRoles={["SuperAdmin", "Curator"]}
          />
        ),
      },
      {
        path: "/super-admin/openrouter-qa/:id",
        element: (
          <ProtectedRoute
            element={<SuperAdminOpenRouterQaDetail />}
            allowedRoles={["SuperAdmin", "Curator"]}
          />
        ),
      },
      {
        path: "/super-admin/update-tracker",
        element: (
          <ProtectedRoute
            element={<SuperAdminUpdateTracker />}
            allowedRoles={["SuperAdmin", "Curator"]}
          />
        ),
      },
      {
        path: "/super-admin/ai-engine-mode-management",
        element: (
          <ProtectedRoute
            element={<SuperAdminAiEngineModeManagement />}
            allowedRoles={["SuperAdmin"]}
          />
        ),
      },
      {
        // SuperAdmin only — the sole Activity Log route; mirrors the backend's requireSuperAdminRole
        // on every /manager-panel/activity-logs endpoint.
        path: "/super-admin/activity-logs",
        element: (
          <ProtectedRoute element={<ActivityLogs />} allowedRoles={["SuperAdmin"]} />
        ),
      },
      {
        path: "/super-admin/billing/invoice",
        element: (
          <ProtectedRoute element={<Invoices />} allowedRoles={["SuperAdmin"]} />
        ),
      },
      {
        path: "/super-admin/settings",
        element: (
          <ProtectedRoute element={<Settings />} allowedRoles={["SuperAdmin"]} />
        ),
      },
      /// agent-admin routes
      {
        path: "/agent/dashboard",
        element: (
          <ProtectedRoute
            element={<AgentDashBoard />}
            allowedRoles={["Agent"]}
            requiredPage="dashboard"
            requireBoth
          />
        ),
      },
      {
        path: "/agent/chatbot",
        element: (
          <ProtectedRoute
            element={<ChatbotUI />}
            allowedRoles={["Agent"]}
          />
        ),
      },
      {
        path: "/agent/my-calls",
        element: (
          <ProtectedRoute element={<MyCalls />} allowedRoles={["Agent"]} requiredPage="interactions" requireBoth />
        ),
      },
      {
        path: "/agent/my-kpis",
        element: (
          <ProtectedRoute element={<MyKpis />} allowedRoles={["Agent"]} requiredPage="dashboard" requireBoth />
        ),
      },
      {
        path: "/agent/daily-tasks",
        element: (
          <ProtectedRoute element={<DailyTask />} allowedRoles={["Agent"]} />
        ),
      },
      {
        path: "/agent/leaderboard",
        element: (
          <ProtectedRoute
            element={<LeaderBoardHome />}
            allowedRoles={["Agent"]}
            requiredPage="leaderboard"
            requireBoth
          />
        ),
      },
      {
        path: "/agent/settings",
        element: (
          <ProtectedRoute element={<Settings />} allowedRoles={["Agent"]} />
        ),
      },
    ],
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/sign-in",
    element: <Login />,
  },
  {
    path: "/sign-up",
    element: <Register />,
  },
  {
    path: "/subscription",
    element: <Subscription />,
  },
  {
    path: "/voice-capture",
    element: <VoiceCapture />,
  },
  {
    path: "/pipeline-status",
    element: <PipelineStatus />,
  },
  {
    path: "*",
    element: <NotFound />
  }
]);

export default router;
